import { c as _c } from "react/compiler-runtime";
import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { gsap } from 'gsap';
export interface MenuItem {
  label: React.ReactNode;
  link?: string;
  ariaLabel?: string;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  external?: boolean;
}
export interface SocialItem {
  label: React.ReactNode;
  link: string;
}
export interface StaggeredMenuProps {
  position?: 'left' | 'right';
  colors?: string[];
  items?: MenuItem[];
  socialItems?: SocialItem[];
  socialTitle?: string;
  displaySocials?: boolean;
  displayItemNumbering?: boolean;
  className?: string;
  logoUrl?: string;
  menuButtonColor?: string;
  openMenuButtonColor?: string;
  accentColor?: string;
  changeMenuColorOnOpen?: boolean;
  isFixed?: boolean;
  closeOnClickAway?: boolean;
  onMenuOpen?: () => void;
  onMenuClose?: () => void;
  onContactClick?: () => void;
  externalOpen?: boolean;
  hideHeader?: boolean;
}
export const StaggeredMenu: React.FC<StaggeredMenuProps> = t0 => {
  const $ = _c(93);
  const {
    position: t1,
    colors: t2,
    items: t3,
    socialItems: t4,
    socialTitle,
    displaySocials: t5,
    displayItemNumbering: t6,
    className,
    logoUrl: t7,
    menuButtonColor: t8,
    openMenuButtonColor: t9,
    accentColor: t10,
    changeMenuColorOnOpen: t11,
    isFixed: t12,
    closeOnClickAway: t13,
    onMenuOpen,
    onMenuClose,
    onContactClick,
    externalOpen,
    hideHeader: t14
  } = t0;
  const position = t1 === undefined ? "right" : t1;
  let t15;
  if ($[0] !== t2) {
    t15 = t2 === undefined ? ["#B497CF", "#5227FF"] : t2;
    $[0] = t2;
    $[1] = t15;
  } else {
    t15 = $[1];
  }
  const colors = t15;
  let t16;
  if ($[2] !== t3) {
    t16 = t3 === undefined ? [] : t3;
    $[2] = t3;
    $[3] = t16;
  } else {
    t16 = $[3];
  }
  const items = t16;
  let t17;
  if ($[4] !== t4) {
    t17 = t4 === undefined ? [] : t4;
    $[4] = t4;
    $[5] = t17;
  } else {
    t17 = $[5];
  }
  const socialItems = t17;
  const displaySocials = t5 === undefined ? true : t5;
  const displayItemNumbering = t6 === undefined ? true : t6;
  const logoUrl = t7 === undefined ? "/src/assets/logos/reactbits-gh-white.svg" : t7;
  const menuButtonColor = t8 === undefined ? "#fff" : t8;
  const openMenuButtonColor = t9 === undefined ? "#fff" : t9;
  const accentColor = t10 === undefined ? "#5227FF" : t10;
  const changeMenuColorOnOpen = t11 === undefined ? true : t11;
  const isFixed = t12 === undefined ? false : t12;
  const closeOnClickAway = t13 === undefined ? true : t13;
  const hideHeader = t14 === undefined ? false : t14;
  const [open, setOpen] = useState(false);
  const openRef = useRef(false);
  const panelRef = useRef(null);
  const preLayersRef = useRef(null);
  let t18;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = [];
    $[6] = t18;
  } else {
    t18 = $[6];
  }
  const preLayerElsRef = useRef(t18);
  const plusHRef = useRef(null);
  const plusVRef = useRef(null);
  const iconRef = useRef(null);
  const textInnerRef = useRef(null);
  let t19;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = ["Menu", "Close"];
    $[7] = t19;
  } else {
    t19 = $[7];
  }
  const [textLines, setTextLines] = useState(t19);
  const openTlRef = useRef(null);
  const closeTweenRef = useRef(null);
  const spinTweenRef = useRef(null);
  const textCycleAnimRef = useRef(null);
  const colorTweenRef = useRef(null);
  const toggleBtnRef = useRef(null);
  const busyRef = useRef(false);
  const itemEntranceTweenRef = useRef(null);
  let t20;
  let t21;
  if ($[8] !== menuButtonColor || $[9] !== position) {
    t20 = () => {
      const ctx = gsap.context(() => {
        const panel = panelRef.current;
        const preContainer = preLayersRef.current;
        const plusH = plusHRef.current;
        const plusV = plusVRef.current;
        const icon = iconRef.current;
        const textInner = textInnerRef.current;
        if (!panel) {
          return;
        }
        let preLayers = [];
        if (preContainer) {
          preLayers = Array.from(preContainer.querySelectorAll(".sm-prelayer"));
        }
        preLayerElsRef.current = preLayers;
        const offscreen = position === "left" ? -100 : 100;
        gsap.set([panel, ...preLayers], {
          xPercent: offscreen,
          opacity: 1
        });
        if (preContainer) {
          gsap.set(preContainer, {
            xPercent: 0,
            opacity: 1
          });
        }
        if (plusH) {
          gsap.set(plusH, {
            transformOrigin: "50% 50%",
            rotate: 0
          });
        }
        if (plusV) {
          gsap.set(plusV, {
            transformOrigin: "50% 50%",
            rotate: 90
          });
        }
        if (icon) {
          gsap.set(icon, {
            rotate: 0,
            transformOrigin: "50% 50%"
          });
        }
        if (textInner) {
          gsap.set(textInner, {
            yPercent: 0
          });
        }
        if (toggleBtnRef.current) {
          gsap.set(toggleBtnRef.current, {
            color: menuButtonColor
          });
        }
      });
      return () => ctx.revert();
    };
    t21 = [menuButtonColor, position];
    $[8] = menuButtonColor;
    $[9] = position;
    $[10] = t20;
    $[11] = t21;
  } else {
    t20 = $[10];
    t21 = $[11];
  }
  useLayoutEffect(t20, t21);
  let t22;
  if ($[12] !== position) {
    t22 = () => {
      const panel_0 = panelRef.current;
      const layers = preLayerElsRef.current;
      if (!panel_0) {
        return null;
      }
      openTlRef.current?.kill();
      if (closeTweenRef.current) {
        closeTweenRef.current.kill();
        closeTweenRef.current = null;
      }
      itemEntranceTweenRef.current?.kill();
      const itemEls = Array.from(panel_0.querySelectorAll(".sm-panel-itemLabel"));
      const numberEls = Array.from(panel_0.querySelectorAll(".sm-panel-list[data-numbering] .sm-panel-item"));
      const socialTitle_0 = panel_0.querySelector(".sm-socials-title");
      const socialLinks = Array.from(panel_0.querySelectorAll(".sm-socials-link"));
      const offscreen_0 = position === "left" ? -100 : 100;
      const layerStates = layers.map(el => ({
        el,
        start: offscreen_0
      }));
      const panelStart = offscreen_0;
      if (itemEls.length) {
        gsap.set(itemEls, {
          yPercent: 140,
          rotate: 10
        });
      }
      if (numberEls.length) {
        gsap.set(numberEls, {
          "--sm-num-opacity": 0
        });
      }
      if (socialTitle_0) {
        gsap.set(socialTitle_0, {
          opacity: 0
        });
      }
      if (socialLinks.length) {
        gsap.set(socialLinks, {
          y: 25,
          opacity: 0
        });
      }
      const tl = gsap.timeline({
        paused: true
      });
      layerStates.forEach((ls, i) => {
        tl.fromTo(ls.el, {
          xPercent: ls.start
        }, {
          xPercent: 0,
          duration: 0.65,
          ease: "power3.out"
        }, i * 0.08);
      });
      const panelInsertTime = layerStates.length * 0.08;
      tl.fromTo(panel_0, {
        xPercent: panelStart
      }, {
        xPercent: 0,
        duration: 0.65,
        ease: "power3.out"
      }, panelInsertTime);
      if (itemEls.length) {
        const itemsStart = panelInsertTime + 0.0975;
        tl.to(itemEls, {
          yPercent: 0,
          rotate: 0,
          duration: 1,
          ease: "power4.out",
          stagger: {
            each: 0.1,
            from: "start"
          }
        }, itemsStart);
        if (numberEls.length) {
          tl.to(numberEls, {
            duration: 0.6,
            ease: "power2.out",
            "--sm-num-opacity": 1,
            stagger: {
              each: 0.08,
              from: "start"
            }
          }, itemsStart + 0.1);
        }
      }
      if (socialTitle_0 || socialLinks.length) {
        const socialsStart = panelInsertTime + 0.26;
        if (socialTitle_0) {
          tl.to(socialTitle_0, {
            opacity: 1,
            duration: 0.5,
            ease: "power2.out"
          }, socialsStart);
        }
        if (socialLinks.length) {
          tl.to(socialLinks, {
            y: 0,
            opacity: 1,
            duration: 0.55,
            ease: "power3.out",
            stagger: {
              each: 0.08,
              from: "start"
            },
            onComplete: () => {
              gsap.set(socialLinks, {
                clearProps: "opacity"
              });
            }
          }, socialsStart + 0.04);
        }
      }
      openTlRef.current = tl;
      return tl;
    };
    $[12] = position;
    $[13] = t22;
  } else {
    t22 = $[13];
  }
  const buildOpenTimeline = t22;
  let t23;
  if ($[14] !== buildOpenTimeline) {
    t23 = () => {
      if (busyRef.current) {
        return;
      }
      busyRef.current = true;
      const tl_0 = buildOpenTimeline();
      if (tl_0) {
        tl_0.eventCallback("onComplete", () => {
          busyRef.current = false;
        });
        tl_0.play(0);
      } else {
        busyRef.current = false;
      }
    };
    $[14] = buildOpenTimeline;
    $[15] = t23;
  } else {
    t23 = $[15];
  }
  const playOpen = t23;
  let t24;
  if ($[16] !== position) {
    t24 = () => {
      openTlRef.current?.kill();
      openTlRef.current = null;
      itemEntranceTweenRef.current?.kill();
      const panel_1 = panelRef.current;
      const layers_0 = preLayerElsRef.current;
      if (!panel_1) {
        return;
      }
      const all = [...layers_0, panel_1];
      closeTweenRef.current?.kill();
      const offscreen_1 = position === "left" ? -100 : 100;
      closeTweenRef.current = gsap.to(all, {
        xPercent: offscreen_1,
        duration: 0.32,
        ease: "power3.in",
        overwrite: "auto",
        onComplete: () => {
          const itemEls_0 = Array.from(panel_1.querySelectorAll(".sm-panel-itemLabel"));
          if (itemEls_0.length) {
            gsap.set(itemEls_0, {
              yPercent: 140,
              rotate: 10
            });
          }
          const numberEls_0 = Array.from(panel_1.querySelectorAll(".sm-panel-list[data-numbering] .sm-panel-item"));
          if (numberEls_0.length) {
            gsap.set(numberEls_0, {
              "--sm-num-opacity": 0
            });
          }
          const socialTitle_1 = panel_1.querySelector(".sm-socials-title");
          const socialLinks_0 = Array.from(panel_1.querySelectorAll(".sm-socials-link"));
          if (socialTitle_1) {
            gsap.set(socialTitle_1, {
              opacity: 0
            });
          }
          if (socialLinks_0.length) {
            gsap.set(socialLinks_0, {
              y: 25,
              opacity: 0
            });
          }
          busyRef.current = false;
        }
      });
    };
    $[16] = position;
    $[17] = t24;
  } else {
    t24 = $[17];
  }
  const playClose = t24;
  let t25;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = opening => {
      const icon_0 = iconRef.current;
      if (!icon_0) {
        return;
      }
      spinTweenRef.current?.kill();
      if (opening) {
        spinTweenRef.current = gsap.to(icon_0, {
          rotate: 225,
          duration: 0.8,
          ease: "power4.out",
          overwrite: "auto"
        });
      } else {
        spinTweenRef.current = gsap.to(icon_0, {
          rotate: 0,
          duration: 0.35,
          ease: "power3.inOut",
          overwrite: "auto"
        });
      }
    };
    $[18] = t25;
  } else {
    t25 = $[18];
  }
  const animateIcon = t25;
  let t26;
  if ($[19] !== changeMenuColorOnOpen || $[20] !== menuButtonColor || $[21] !== openMenuButtonColor) {
    t26 = opening_0 => {
      const btn = toggleBtnRef.current;
      if (!btn) {
        return;
      }
      colorTweenRef.current?.kill();
      if (changeMenuColorOnOpen) {
        const targetColor = opening_0 ? openMenuButtonColor : menuButtonColor;
        colorTweenRef.current = gsap.to(btn, {
          color: targetColor,
          delay: 0.18,
          duration: 0.3,
          ease: "power2.out"
        });
      } else {
        gsap.set(btn, {
          color: menuButtonColor
        });
      }
    };
    $[19] = changeMenuColorOnOpen;
    $[20] = menuButtonColor;
    $[21] = openMenuButtonColor;
    $[22] = t26;
  } else {
    t26 = $[22];
  }
  const animateColor = t26;
  let t27;
  let t28;
  if ($[23] !== changeMenuColorOnOpen || $[24] !== menuButtonColor || $[25] !== openMenuButtonColor) {
    t27 = () => {
      if (toggleBtnRef.current) {
        if (changeMenuColorOnOpen) {
          const targetColor_0 = openRef.current ? openMenuButtonColor : menuButtonColor;
          gsap.set(toggleBtnRef.current, {
            color: targetColor_0
          });
        } else {
          gsap.set(toggleBtnRef.current, {
            color: menuButtonColor
          });
        }
      }
    };
    t28 = [changeMenuColorOnOpen, menuButtonColor, openMenuButtonColor];
    $[23] = changeMenuColorOnOpen;
    $[24] = menuButtonColor;
    $[25] = openMenuButtonColor;
    $[26] = t27;
    $[27] = t28;
  } else {
    t27 = $[26];
    t28 = $[27];
  }
  React.useEffect(t27, t28);
  let t29;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = opening_1 => {
      const inner = textInnerRef.current;
      if (!inner) {
        return;
      }
      textCycleAnimRef.current?.kill();
      const currentLabel = opening_1 ? "Menu" : "Close";
      const targetLabel = opening_1 ? "Close" : "Menu";
      const seq = [currentLabel];
      let last = currentLabel;
      for (let i_0 = 0; i_0 < 3; i_0++) {
        last = last === "Menu" ? "Close" : "Menu";
        seq.push(last);
      }
      if (last !== targetLabel) {
        seq.push(targetLabel);
      }
      seq.push(targetLabel);
      setTextLines(seq);
      gsap.set(inner, {
        yPercent: 0
      });
      const lineCount = seq.length;
      const finalShift = (lineCount - 1) / lineCount * 100;
      textCycleAnimRef.current = gsap.to(inner, {
        yPercent: -finalShift,
        duration: 0.5 + lineCount * 0.07,
        ease: "power4.out"
      });
    };
    $[28] = t29;
  } else {
    t29 = $[28];
  }
  const animateText = t29;
  let t30;
  let t31;
  if ($[29] !== animateColor || $[30] !== externalOpen || $[31] !== onMenuClose || $[32] !== onMenuOpen || $[33] !== playClose || $[34] !== playOpen) {
    t30 = () => {
      if (externalOpen !== undefined && externalOpen !== openRef.current) {
        openRef.current = externalOpen;
        setOpen(externalOpen);
        if (externalOpen) {
          onMenuOpen?.();
          playOpen();
        } else {
          onMenuClose?.();
          playClose();
        }
        animateIcon(externalOpen);
        animateColor(externalOpen);
        animateText(externalOpen);
      }
    };
    t31 = [externalOpen, playOpen, playClose, animateIcon, animateColor, animateText, onMenuOpen, onMenuClose];
    $[29] = animateColor;
    $[30] = externalOpen;
    $[31] = onMenuClose;
    $[32] = onMenuOpen;
    $[33] = playClose;
    $[34] = playOpen;
    $[35] = t30;
    $[36] = t31;
  } else {
    t30 = $[35];
    t31 = $[36];
  }
  React.useEffect(t30, t31);
  let t32;
  if ($[37] !== animateColor || $[38] !== onMenuClose || $[39] !== onMenuOpen || $[40] !== playClose || $[41] !== playOpen) {
    t32 = () => {
      const target = !openRef.current;
      openRef.current = target;
      setOpen(target);
      if (target) {
        onMenuOpen?.();
        playOpen();
      } else {
        toggleBtnRef.current?.focus();
        onMenuClose?.();
        playClose();
      }
      animateIcon(target);
      animateColor(target);
      animateText(target);
    };
    $[37] = animateColor;
    $[38] = onMenuClose;
    $[39] = onMenuOpen;
    $[40] = playClose;
    $[41] = playOpen;
    $[42] = t32;
  } else {
    t32 = $[42];
  }
  const toggleMenu = t32;
  let t33;
  if ($[43] !== animateColor || $[44] !== externalOpen || $[45] !== onMenuClose || $[46] !== playClose) {
    t33 = () => {
      if (openRef.current) {
        if (externalOpen !== undefined) {
          onMenuClose?.();
        } else {
          toggleBtnRef.current?.focus();
          openRef.current = false;
          setOpen(false);
          onMenuClose?.();
          playClose();
          animateIcon(false);
          animateColor(false);
          animateText(false);
        }
      }
    };
    $[43] = animateColor;
    $[44] = externalOpen;
    $[45] = onMenuClose;
    $[46] = playClose;
    $[47] = t33;
  } else {
    t33 = $[47];
  }
  const closeMenu = t33;
  let t34;
  let t35;
  if ($[48] !== closeMenu || $[49] !== closeOnClickAway || $[50] !== open) {
    t34 = () => {
      if (!closeOnClickAway || !open) {
        return;
      }
      const handleClickOutside = event => {
        if (panelRef.current && !panelRef.current.contains(event.target as Node) && toggleBtnRef.current && !toggleBtnRef.current.contains(event.target as Node)) {
          closeMenu();
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    };
    t35 = [closeOnClickAway, open, closeMenu];
    $[48] = closeMenu;
    $[49] = closeOnClickAway;
    $[50] = open;
    $[51] = t34;
    $[52] = t35;
  } else {
    t34 = $[51];
    t35 = $[52];
  }
  React.useEffect(t34, t35);
  const t36 = (className ? className + " " : "") + "staggered-menu-wrapper" + (isFixed ? " fixed-wrapper" : "");
  let t37;
  if ($[53] !== accentColor) {
    t37 = accentColor ? {
      "--sm-accent": accentColor
    } as React.CSSProperties & Record<'--sm-accent', string> : undefined;
    $[53] = accentColor;
    $[54] = t37;
  } else {
    t37 = $[54];
  }
  const t38 = open || undefined;
  let t39;
  if ($[55] !== colors) {
    t39 = colors && colors.length > 0 ? colors : ["var(--muted)", "var(--border)"];
    $[55] = colors;
    $[56] = t39;
  } else {
    t39 = $[56];
  }
  const arr = t39;
  let t40;
  if ($[57] !== arr) {
    t40 = arr.map(_temp);
    $[57] = arr;
    $[58] = t40;
  } else {
    t40 = $[58];
  }
  const t41 = t40;
  let t42;
  if ($[59] !== t41) {
    t42 = <div ref={preLayersRef} className="sm-prelayers" aria-hidden="true">{t41}</div>;
    $[59] = t41;
    $[60] = t42;
  } else {
    t42 = $[60];
  }
  let t43;
  if ($[61] !== hideHeader || $[62] !== logoUrl || $[63] !== onContactClick || $[64] !== open || $[65] !== textLines || $[66] !== toggleMenu) {
    t43 = !hideHeader && <header className="staggered-menu-header" aria-label="Main navigation header"><div className="sm-logo" aria-label="Logo">{logoUrl ? <Image src={logoUrl} alt="Logo" className="sm-logo-img" draggable={false} width={110} height={24} /> : <span className="text-xl font-bold tracking-tight text-foreground">Michael Medhat</span>}</div><div className="flex items-center gap-4 pointer-events-auto">{onContactClick && <button onClick={onContactClick} className="px-3 py-1.5 border border-foreground text-[10px] font-mono uppercase tracking-widest text-foreground hover:bg-foreground hover:text-background transition-all duration-300 pointer-events-auto cursor-pointer">Contact</button>}<button ref={toggleBtnRef} className="sm-toggle text-foreground pointer-events-auto" aria-label={open ? "Close menu" : "Open menu"} aria-expanded={open} aria-controls="staggered-menu-panel" onClick={toggleMenu} type="button"><span className="sm-toggle-textWrap" aria-hidden="true"><span ref={textInnerRef} className="sm-toggle-textInner">{textLines.map(_temp2)}</span></span><span ref={iconRef} className="sm-icon" aria-hidden="true"><span ref={plusHRef} className="sm-icon-line" /><span ref={plusVRef} className="sm-icon-line sm-icon-line-v" /></span></button></div></header>;
    $[61] = hideHeader;
    $[62] = logoUrl;
    $[63] = onContactClick;
    $[64] = open;
    $[65] = textLines;
    $[66] = toggleMenu;
    $[67] = t43;
  } else {
    t43 = $[67];
  }
  const t44 = !open;
  const t45 = !open || undefined;
  const t46 = displayItemNumbering || undefined;
  let t47;
  if ($[68] !== closeMenu || $[69] !== items) {
    t47 = items && items.length ? items.map((it, idx) => <li className="sm-panel-itemWrap" key={idx}>{it.external ? <a className="sm-panel-item" href={it.link || "#"} onClick={e => {
        if (it.onClick) {
          it.onClick(e);
        }
        closeMenu();
      }} aria-label={it.ariaLabel} data-index={idx + 1} target="_blank" rel="noopener noreferrer"><span className="sm-panel-itemLabel">{it.label}</span></a> : <Link className="sm-panel-item" href={it.link || "#"} prefetch={it.link?.includes("#") ? false : undefined} onClick={e_0 => {
        if (it.onClick) {
          it.onClick(e_0 as unknown as React.MouseEvent<HTMLAnchorElement>);
        }
        closeMenu();
      }} aria-label={it.ariaLabel} data-index={idx + 1}><span className="sm-panel-itemLabel">{it.label}</span></Link>}</li>) : <li className="sm-panel-itemWrap" aria-hidden="true"><span className="sm-panel-item"><span className="sm-panel-itemLabel">No items</span></span></li>;
    $[68] = closeMenu;
    $[69] = items;
    $[70] = t47;
  } else {
    t47 = $[70];
  }
  let t48;
  if ($[71] !== t46 || $[72] !== t47) {
    t48 = <ul className="sm-panel-list" role="list" data-numbering={t46}>{t47}</ul>;
    $[71] = t46;
    $[72] = t47;
    $[73] = t48;
  } else {
    t48 = $[73];
  }
  let t49;
  if ($[74] !== displaySocials || $[75] !== socialItems || $[76] !== socialTitle) {
    t49 = displaySocials && socialItems && socialItems.length > 0 && <div className="sm-socials" aria-label="Social links"><h3 className="sm-socials-title font-extrabold uppercase tracking-wider text-sm">{socialTitle || "SOCIALS"}</h3><ul className="sm-socials-list mt-2.5" role="list">{socialItems.map(_temp3)}</ul></div>;
    $[74] = displaySocials;
    $[75] = socialItems;
    $[76] = socialTitle;
    $[77] = t49;
  } else {
    t49 = $[77];
  }
  let t50;
  if ($[78] !== t48 || $[79] !== t49) {
    t50 = <div className="sm-panel-inner">{t48}{t49}</div>;
    $[78] = t48;
    $[79] = t49;
    $[80] = t50;
  } else {
    t50 = $[80];
  }
  let t51;
  if ($[81] !== t44 || $[82] !== t45 || $[83] !== t50) {
    t51 = <aside id="staggered-menu-panel" ref={panelRef} className="staggered-menu-panel" aria-hidden={t44} inert={t45}>{t50}</aside>;
    $[81] = t44;
    $[82] = t45;
    $[83] = t50;
    $[84] = t51;
  } else {
    t51 = $[84];
  }
  let t52;
  if ($[85] !== position || $[86] !== t36 || $[87] !== t37 || $[88] !== t38 || $[89] !== t42 || $[90] !== t43 || $[91] !== t51) {
    t52 = <div className={t36} style={t37} data-position={position} data-open={t38}>{t42}{t43}{t51}</div>;
    $[85] = position;
    $[86] = t36;
    $[87] = t37;
    $[88] = t38;
    $[89] = t42;
    $[90] = t43;
    $[91] = t51;
    $[92] = t52;
  } else {
    t52 = $[92];
  }
  return t52;
};
export default StaggeredMenu;
function _temp(c, i_1) {
  return <div key={i_1} className="sm-prelayer" style={{
    background: c
  }} />;
}
function _temp2(l, i_2) {
  return <span className="sm-toggle-line" key={i_2}>{l}</span>;
}
function _temp3(s, i_3) {
  return <li key={i_3} className="sm-socials-item"><a href={s.link} target="_blank" rel="noopener noreferrer" className="sm-socials-link w-10 h-10 flex items-center justify-center border-2 border-[#ef4444] bg-transparent text-[#ef4444] hover:bg-[#ef4444] hover:text-black transition-all duration-300 pointer-events-auto brutalist-shadow-static active:translate-x-0.5 active:translate-y-0.5 active:shadow-none">{s.label}</a></li>;
}